/// <reference path='common.ts' />
/// <reference path='element_form.ts' />
/// <reference path='element_table.ts' />

/**
 * 汎用フェッチクラスです。
 */
class HaoriFetch {
  /** セッションストレージのキーのプレフィックス */
  private static STORAGE_KEY = "HaoriCache_";

  /** HTTPメソッド */
  method: string;

  /** Content-Type */
  enctype: string | null;

  /** フェッチに関連付けられたフォームエレメント（paramと排他） */
  form: HTMLFormElement;

  /** フェッチに成功した場合にリセットするフォーム */
  resetForms: Array<HTMLFormElement> | NodeListOf<HTMLFormElement>;

  /** 送信パラメータ（formと排他） */
  param: any;

  /** 確認メッセージ */
  confirmMessage: string;

  /** データ通信完了時のアラートメッセージ */
  successAlertMessage: string;

  /** データ通信完了時のメッセージ */
  successMessage: string;

  /** データ通信完了時のリダイレクト先URL */
  redirectUrl: string;

  /** 通信結果を格納するエレメント */
  bindElements: Element[];

  /** 通信結果を格納する属性名 */
  bindAttribute = "data-bind";

  /** 通信結果を格納する変数名 */
  bindArg: string | null = null;

  /** 通信結果を元のdata-bindとマージする */
  bindMerge: boolean | null = null;

  /** 通信結果をダウンロードする場合のファイル名 */
  downloadFileName: string;

  /** データ通信完了時に開くダイアログ */
  openDialog: Element;

  /** データ通信完了時に閉じるダイアログ */
  closeDialog: Element;

  /** データ通信完了時にクリックするエレメントのリスト */
  clickElements: HTMLElement[];

  /** データ通信完了時にリロードするbindエレメントのリスト */
  reloadElements: HTMLElement[];

  /** データ通信完了時にリロードするテーブルエレメントのリスト */
  tableElements: HTMLTableElement[];

  /** データ通信完了時にdata-bind属性をコピーするエレメントのリスト */
  copyElements: HTMLElement[];

  /** キャッシュのキー文字列 */
  cacheKey: string;

  /** データ通信完了時にキャッシュを使用する場合はtrue */
  useCache = false;

  /** データ通信完了時にキャッシュを保存する場合はtrue */
  setCache = false;

  /** データ通信完了時にキャッシュを削除する場合はtrue */
  clearCache = false;

  /** データ通信完了時にスクロールして表示させるエレメント */
  scrollElement: HTMLElement;

  /**
   * コンストラクタ。
   */
  constructor() {}

  /**
   * アトリビュートから設定を取得します。
   *
   * @param element 対象エレメント
   * @param prefix 属性の接頭語
   */
  setParams(element: Element, prefix: string) {
    Haori.hasAttribute(element, prefix + "-bind", (element, value) => {
      if (value == "") {
        this.bindElements = [element];
      } else {
        this.bindElements = Array.from(Haori.querySelectorAll(value));
      }
    });
    Haori.hasAttribute(element, prefix + "-bind-arg", (element, value) => {
      this.bindArg = value;
    });
    Haori.hasAttribute(element, prefix + "-bind-merge", (element, value) => {
      this.bindMerge = true;
    });
    Haori.hasAttribute(
      element,
      prefix + "-bind-attribute",
      (element, value) => {
        this.bindAttribute = value;
      }
    );
    Haori.hasAttribute(element, prefix + "-alert", (element, value) => {
      this.successAlertMessage = value;
    });
    Haori.hasAttribute(element, prefix + "-message", (element, value) => {
      this.successMessage = value;
    });
    Haori.hasAttribute(element, prefix + "-redirect", (element, value) => {
      this.redirectUrl = value;
    });
    Haori.hasAttribute(element, prefix + "-download", (element, value) => {
      this.downloadFileName = value;
    });
    Haori.hasAttribute(element, prefix + "-open", (element, value) => {
      let dialog = Haori.findDialog(element, value);
      if (dialog) {
        this.openDialog = dialog;
        dialog.querySelectorAll("form").forEach((form: HTMLFormElement) => {
          HaoriForm.clearAllMessages(form);
        });
      }
    });
    Haori.hasAttribute(element, prefix + "-close", (element, value) => {
      let dialog = Haori.findDialog(element, value);
      if (dialog) {
        this.closeDialog = dialog;
      }
    });
    Haori.hasAttribute(element, prefix + "-click", (element, value) => {
      this.clickElements = <HTMLElement[]>(
        Array.from(Haori.querySelectorAll(value))
      );
    });
    Haori.hasAttribute(element, prefix + "-bind-reload", (element, value) => {
      this.reloadElements = <HTMLElement[]>(
        Array.from(Haori.querySelectorAll(value))
      );
    });
    Haori.hasAttribute(element, prefix + "-table-reload", (element, value) => {
      this.tableElements = <HTMLTableElement[]>(
        Array.from(Haori.querySelectorAll(value))
      );
    });
    Haori.hasAttribute(element, prefix + "-copy", (element, value) => {
      this.copyElements = <HTMLElement[]>(
        Array.from(Haori.querySelectorAll(value))
      );
    });
    Haori.hasAttribute(element, prefix + "-form", (element, value) => {
      if (value == "") {
        this.form = <HTMLFormElement>element.closest("form");
      } else {
        this.form = <HTMLFormElement>Haori.querySelector(value);
      }
    });
    Haori.hasAttribute(element, prefix + "-reset", (element, value) => {
      if (value) {
        this.resetForms = <NodeListOf<HTMLFormElement>>(
          Haori.querySelectorAll(value)
        );
      } else {
        this.resetForms = [this.form];
      }
    });
    Haori.hasAttribute(element, prefix + "-param", (element, value) => {
      this.param = JSON.parse(value);
    });
    Haori.hasAttribute(element, prefix + "-method", (element, value) => {
      this.method = value;
    });
    if (!this.method) {
      this.method = "GET";
    }
    Haori.hasAttribute(element, prefix + "-enctype", (element, value) => {
      this.enctype = value;
    });
    if (!this.enctype) {
      this.enctype =
        this.method == "GET"
          ? "application/x-www-form-urlencoded"
          : "application/json";
    }
    Haori.hasAttribute(element, prefix + "-cache-use", (element, value) => {
      this.useCache = true;
      if (value != "") {
        this.cacheKey = value;
      }
    });
    Haori.hasAttribute(element, prefix + "-cache-set", (element, value) => {
      this.setCache = true;
      if (value != "") {
        this.cacheKey = value;
      }
    });
    Haori.hasAttribute(element, prefix + "-cache-clear", (element, value) => {
      this.clearCache = true;
      if (value != "") {
        this.cacheKey = value;
      }
    });
    Haori.hasAttribute(element, prefix + "-scroll", (element, value) => {
      this.scrollElement = <HTMLElement>Haori.querySelector(value);
    });
  }

  /**
   * エレメントにバインドデータを設定します。
   *
   * @param elements 設定対象のエレメントのリスト
   * @param attribute 設定アトリビュート名
   * @param data データ
   */
  setBindAttribute(elements: Element[], attribute: string, data: any) {
    if (elements) {
      elements.forEach((target: Element) => {
        if (this.bindArg) {
          let object: any;
          if (target.hasAttribute(attribute)) {
            object = JSON.parse(target.getAttribute(attribute)!);
          } else {
            object = {};
          }
          if (this.bindMerge) {
            object[this.bindArg] = this.mergeObject(object[this.bindArg], data);
          } else {
            object[this.bindArg] = data;
          }
          target.setAttribute(attribute, JSON.stringify(object));
        } else {
          if (this.bindMerge) {
            let object = JSON.parse(target.getAttribute(attribute)!);
            object = this.mergeObject(object, data);
            target.setAttribute(attribute, JSON.stringify(object));
          } else {
            target.setAttribute(attribute, JSON.stringify(data));
          }
        }
      });
    }
  }

  /**
   * sourceオブジェクトにtargetオブジェクトの要素をマージします。
   *
   * @param source マージ元オブジェクト
   * @param target マージ先オブジェクト
   */
  mergeObject(source: any, target: any): any {
    if (source == null) {
      source = {};
    }
    if (target == null) {
      return source;
    }
    for (let key in target) {
      source[key] = target[key];
    }
    return source;
  }

  /**
   * 通信を行います。URLに{{や}}がある場合は通信を行いません。
   *
   * @param url 通信先URL
   * @param element 処理対象のエレメント（通信結果が属性に格納される）
   * @return 何らかの処理を行った場合はtrue（確認ダイアログでキャンセルされてもtrue）
   */
  async fetch(url: string, element: Element): Promise<boolean> {
    if (this.confirmMessage) {
      let result = await Haori.confirm(this.confirmMessage);
      if (result === false) {
        return true;
      }
    }
    if (url.indexOf("{{") >= 0 && url.indexOf("}}") >= 0) {
      return false;
    }

    if (!this.cacheKey) {
      this.cacheKey = url;
    }
    if (this.useCache) {
      let json = sessionStorage.getItem(HaoriFetch.STORAGE_KEY + this.cacheKey);
      if (json) {
        let data = JSON.parse(json);
        if (this.resetForms) {
          this.resetForms.forEach((form: HTMLFormElement) => {
            HaoriForm.reset(form, true);
          });
        }
        this.setBindAttribute(this.bindElements, this.bindAttribute, data);
        this.success(data, true);
        return true;
      }
    }

    let param: any;
    if (this.form) {
      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return false;
      }
      param = HaoriForm.getValues(this.form);
    }
    if (this.param) {
      param = this.param;
    }

    let body: string | FormData | null = null;
    if (!this.method) {
      this.method = "GET";
    }
    if (!this.enctype) {
      this.enctype = "application/x-www-form-urlencoded";
    }
    if (this.method.toUpperCase() == "GET") {
      let queryParams = HaoriForm.getQueryParameterFromObject(param);
      if (queryParams.length > 0) {
        url += (url.indexOf("?") < 0 ? "?" : "&") + queryParams;
      }
    } else {
      if (this.enctype == "application/json") {
        body = JSON.stringify(param);
        if (body != null && body.length == 0) {
          body = null;
        }
      } else if (this.enctype == "multipart/form-data") {
        body = HaoriForm.getFormData(this.form);
        this.enctype = null; // マルチパートの場合はバウンダリが自動的に設定されるので Content-Type を明示しない
      } else {
        body = HaoriForm.getQueryParameterFromObject(param);
        if (body != null && body.length == 0) {
          body = null;
        }
      }
    }

    let request: RequestInit = {
      method: this.method,
      body: body,
    };
    if (this.enctype) {
      request.headers = {
        "Content-Type": this.enctype,
      };
    }

    if (this.form != null) {
      HaoriForm.disableButtons(this.form);
      HaoriForm.clearAllMessages(this.form);
    }

    Haori.fetch(
      this.bindElements || [element],
      url,
      request,
      (response, data) => {
        if (this.form != null) {
          HaoriForm.enableButtons(this.form);
        }
        if (this.resetForms) {
          this.resetForms.forEach((form: HTMLFormElement) => {
            HaoriForm.reset(form, true);
          });
        }
        this.setBindAttribute(
          this.bindElements,
          this.bindAttribute,
          response.ok ? data : {}
        );
        if (response.ok) {
          if (this.setCache) {
            sessionStorage.setItem(
              HaoriFetch.STORAGE_KEY + this.cacheKey,
              JSON.stringify(data)
            );
          }
          this.success(data, true);
        } else {
          data = Haori.options.errorMessageConverter(
            url,
            request,
            response,
            data
          );
          if (data == null) {
            return;
          }
          if (this.form) {
            if (Array.isArray(data)) {
              data.forEach((object: HashObject) => {
                HaoriForm.addErrorMessage(
                  <HTMLFormElement>this.form,
                  object.key,
                  object.message
                );
              });
            } else {
              HaoriForm.addErrorMessage(this.form, null, data);
            }
          } else {
            if (Array.isArray(data)) {
              data.forEach((object: HashObject) => {
                Haori.message(object.message, HaoriMessageLevel.DANGER);
              });
            } else {
              Haori.message(data.toString(), HaoriMessageLevel.DANGER);
            }
          }
        }
      },
      (reason) => {
        if (reason) {
          HaoriForm.addErrorMessage(this.form, null, reason);
        }
        if (this.form != null) {
          HaoriForm.enableButtons(this.form);
        }
      }
    );
    return true;
  }

  /**
   * 通信の代わりにデータを受信したこととして処理を行います。
   *
   * @param data データ
   * @return 何らかの処理を行った場合はtrue（確認ダイアログでキャンセルされてもtrue）
   */
  async receive(data: any): Promise<boolean> {
    if (this.confirmMessage) {
      let result = await Haori.confirm(this.confirmMessage);
      if (result === false) {
        return true;
      }
    }
    if (this.resetForms) {
      this.resetForms.forEach((form: HTMLFormElement) => {
        HaoriForm.reset(form, true);
      });
    }
    if (this.bindElements && data != null) {
      this.setBindAttribute(this.bindElements, this.bindAttribute, data);
    }
    return this.success(data, false);
  }

  /**
   * 通信成功処理を行います。
   *
   * @param data データ
   * @param fetched データ通信を行った場合はtrue
   * @return 処理を行った場合はtrue
   */
  private success(data: any, fetched: boolean): boolean {
    let result = false;
    if (this.clearCache) {
      sessionStorage.removeItem(HaoriFetch.STORAGE_KEY + this.cacheKey);
      result = true;
    }
    let promise: Promise<void> | null = null;
    if (this.successAlertMessage) {
      promise = Haori.alert(this.successAlertMessage);
      result = true;
    }
    if (this.successMessage) {
      if (fetched) {
        promise = Haori.message(this.successMessage, HaoriMessageLevel.SUCCESS);
      } else {
        promise = Haori.message(this.successMessage, HaoriMessageLevel.INFO);
      }
      result = true;
    }
    if (this.downloadFileName) {
      let anchor = document.createElement("a");
      var blob = new Blob([data]);
      anchor.href = URL.createObjectURL(blob);
      anchor.download = this.downloadFileName;
      anchor.click();
      result = true;
    }
    if (this.copyElements) {
      this.setBindAttribute(this.copyElements, "data-bind", data);
    }
    if (this.openDialog) {
      Haori.options.openDialog(this.openDialog);
      result = true;
    }
    if (this.closeDialog) {
      Haori.options.closeDialog(this.closeDialog);
      result = true;
    }
    let stopped = false;
    if (this.clickElements) {
      this.clickElements.forEach((element: HTMLElement) => {
        let event = new CustomEvent("click", {
          bubbles: true,
          cancelable: true,
          composed: true,
        });
        element.dispatchEvent(event);
        if (event.defaultPrevented) {
          stopped = true;
        }
        result = true;
      });
    }
    if (stopped) {
      return result;
    }
    if (this.reloadElements) {
      this.reloadElements.forEach((element: HTMLElement) => {
        element.removeAttribute("data-bind-fetch-current");
        HaoriBind.doFetch(element);
        result = true;
      });
    }
    if (this.tableElements) {
      this.tableElements.forEach((element: HTMLTableElement) => {
        HaoriTable.fetch(element);
        result = true;
      });
    }
    if (this.redirectUrl) {
      let url = this.redirectUrl;
      if (!fetched) {
        let param: any;
        if (this.form) {
          param = HaoriForm.getValues(this.form);
        }
        if (this.param) {
          param = this.param;
        }
        let queryParams = HaoriForm.getQueryParameterFromObject(param);
        if (queryParams.length > 0) {
          url += (url.indexOf("?") < 0 ? "?" : "&") + queryParams;
        }
      }
      if (promise) {
        promise.then(() => {
          location.href = url;
        });
      } else {
        location.href = url;
      }
      result = true;
    }
    if (this.scrollElement) {
      let rect = this.scrollElement.getBoundingClientRect();
      let toLeft = 0;
      if (rect.left < 0) {
        toLeft = 0 - rect.left;
      } else if (rect.right > window.innerWidth) {
        toLeft = window.innerWidth - rect.right;
      }
      let toTop = 0;
      if (rect.top < 0) {
        toTop = rect.top;
      } else if (rect.bottom > window.innerHeight) {
        toTop = rect.bottom - window.innerHeight;
      }
      window.scrollBy({
        top: toTop,
        left: toLeft,
        behavior: "smooth",
      });
    }
    return result;
  }
}
